import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  ["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"],
  ["thursday", "Jeudi"], ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"],
];

export default function Availability() {
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get("/availability").then((r) => setA(r.data && r.data.weekdays ? r.data : {
      weekdays: Object.fromEntries(DAYS.map(([k]) => [k, { enabled: k !== "sunday", open: "09:00", close: "18:00" }])),
      default_duration: 60, min_notice_minutes: 120, max_per_day: 8, unavailable_dates: [],
    }));
  }, []);

  if (!a) return <div className="text-slate-500">Chargement…</div>;

  async function save() {
    await api.put("/availability", {
      weekdays: a.weekdays,
      default_duration: Number(a.default_duration),
      min_notice_minutes: Number(a.min_notice_minutes),
      max_per_day: Number(a.max_per_day),
      unavailable_dates: a.unavailable_dates,
    });
    toast.success("Disponibilités enregistrées");
  }

  function setDay(k, patch) {
    setA({ ...a, weekdays: { ...a.weekdays, [k]: { ...a.weekdays[k], ...patch } } });
  }

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold text-[#16324F]">Disponibilités</h1>
      <p className="mt-1 text-slate-500">Ces horaires alimentent directement les créneaux proposés à vos clients.</p>

      <section className="mt-8 kf-card p-6">
        <h2 className="font-display font-bold text-lg text-[#16324F]">Jours travaillés</h2>
        <div className="mt-4 grid gap-3">
          {DAYS.map(([k, label]) => (
            <div key={k} className="grid grid-cols-[120px_80px_1fr_1fr] gap-3 items-center" data-testid={`day-${k}`}>
              <div className="font-semibold text-[#16324F]">{label}</div>
              <div className="flex items-center gap-2"><Switch checked={!!a.weekdays[k]?.enabled} onCheckedChange={(v) => setDay(k, { enabled: v })}/> <span className="text-xs text-slate-500">{a.weekdays[k]?.enabled ? "Ouvert" : "Fermé"}</span></div>
              <Input type="time" value={a.weekdays[k]?.open || "09:00"} onChange={(e) => setDay(k, { open: e.target.value })} className="h-10" disabled={!a.weekdays[k]?.enabled}/>
              <Input type="time" value={a.weekdays[k]?.close || "18:00"} onChange={(e) => setDay(k, { close: e.target.value })} className="h-10" disabled={!a.weekdays[k]?.enabled}/>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6 kf-card p-6 grid md:grid-cols-3 gap-4">
        <div className="grid gap-1.5"><Label>Durée par défaut (min)</Label><Input type="number" value={a.default_duration} onChange={(e) => setA({...a, default_duration: e.target.value})} data-testid="default-duration"/></div>
        <div className="grid gap-1.5"><Label>Délai de préavis (min)</Label><Input type="number" value={a.min_notice_minutes} onChange={(e) => setA({...a, min_notice_minutes: e.target.value})} data-testid="min-notice"/></div>
        <div className="grid gap-1.5"><Label>Max RDV / jour</Label><Input type="number" value={a.max_per_day} onChange={(e) => setA({...a, max_per_day: e.target.value})} data-testid="max-per-day"/></div>
      </section>

      <section className="mt-6 kf-card p-6">
        <h2 className="font-display font-bold text-lg text-[#16324F]">Dates non disponibles</h2>
        <p className="text-xs text-slate-500 mt-1">Une date par ligne au format AAAA-MM-JJ.</p>
        <textarea
          value={(a.unavailable_dates || []).join("\n")}
          onChange={(e) => setA({...a, unavailable_dates: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean)})}
          rows={4}
          className="mt-3 w-full rounded-lg border border-slate-200 p-3 font-mono text-sm"
          data-testid="unavailable-dates"
        />
      </section>

      <Button onClick={save} className="mt-6 rounded-full bg-[#16324F] hover:bg-[#0F2438]" data-testid="save-availability"><Save className="w-4 h-4 mr-1"/>Enregistrer</Button>
    </div>
  );
}
